// src/pages/TransactionDetails.js
import React, { useState } from 'react';
import {
    Container,
    Typography,
    Paper,
    Grid,
    CircularProgress,
    Box,
    TextField,
    IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useParams, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { format } from 'date-fns';

import ProgressBar from '../components/ProgressBar';
import ItemCard from '../components/ItemCard';
import OwnerInfoBox from '../components/OwnerInfoBox';
import BookingInfoCard from '../components/BookingInfoCard';
import ActionButtons from '../components/ActionButtons';
import ReviewForm from '../components/ReviewForm';
import BookingDetailContainer from '../components/BookingDetailContainer';
import ChatMessagesBox from '../components/ChatMessagesBox';
import { useAuth } from '../context/AuthContext';
import { submitReview } from '../services/api';

const StyledContainer = styled(Container)(({ theme }) => ({
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    borderRadius: theme.spacing(2),
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)',
}));

function TransactionDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const [reviewSubmitted, setReviewSubmitted] = useState(false);

    const handleItemClick = (listingData) => {
        navigate(`/listings/${listingData?.uuid}`);
    };

    const handleReviewSubmit = async (reviewData, transaction, setSnackbar) => {
        try {
            await submitReview(transaction.listing_id, {
                ...reviewData,
                transaction_id: transaction.id,
            });
            setSnackbar({ open: true, message: 'Review submitted successfully!', severity: 'success' });
            setReviewSubmitted(true);
        } catch (err) {
            console.error('Error submitting review:', err);
            setSnackbar({ open: true, message: 'Error submitting review', severity: 'error' });
        }
    };

    return (
        <BookingDetailContainer transactionId={id}>
            {({
                transaction,
                loading,
                newMessage,
                setNewMessage,
                handleSendMessage,
                handleUpdateStatus,
                getStepStatus,
                otherUserInfo,
                listingData,
                messages,
                snackbar,
                setSnackbar,
            }) => {
                if (loading) {
                    return (
                        <StyledContainer maxWidth="md">
                            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                                <CircularProgress />
                            </Box>
                        </StyledContainer>
                    );
                }
                const userRole = currentUser.id === transaction.lender_id ? 'lender' : 'borrower';
                return (
                    <StyledContainer maxWidth="md">
                        <Box display="flex" alignItems="center" mb={3}>
                            <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
                                <ArrowBackIcon />
                            </IconButton>
                            <Typography variant="h5" component="h1">
                                Booking Request Details
                            </Typography>
                        </Box>
                        <Grid container spacing={3}>
                            {/* LEFT SIDE */}
                            <Grid item xs={12} md={7}>
                                <StyledPaper>
                                    <ProgressBar steps={getStepStatus()} />
                                    <Typography variant="h6" gutterBottom>
                                        {transaction?.status === 'pending'
                                            ? userRole === 'lender'
                                                ? 'New booking request received'
                                                : 'Booking request sent'
                                            : `Booking ${transaction?.status}`}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" gutterBottom>
                                        {transaction?.status === 'pending'
                                            ? userRole === 'lender'
                                                ? 'Please respond to this request promptly.'
                                                : 'Waiting for the lender to respond.'
                                            : `Last updated: ${format(new Date(transaction?.updated_at), 'MMM d, yyyy HH:mm')}`}
                                    </Typography>
                                    <ItemCard
                                        imageSrc={listingData?.image_url}
                                        title={listingData?.title}
                                        description={listingData?.description}
                                        price={listingData?.price}
                                        onClick={() => handleItemClick(listingData)}
                                    />
                                    <OwnerInfoBox
                                        profilePicture={otherUserInfo?.profile_picture}
                                        firstName={otherUserInfo?.first_name}
                                        lastName={otherUserInfo?.last_name}
                                        averageRating={otherUserInfo?.average_rating}
                                        zipCode={otherUserInfo?.zip_code}
                                        userId={transaction?.lender_id}
                                    />
                                    <BookingInfoCard
                                        startDate={transaction?.start_date}
                                        endDate={transaction?.end_date}
                                        bookingNumber={transaction?.id}
                                        totalPrice={transaction?.total_transaction_amount}
                                    />
                                    <ActionButtons
                                        userRole={userRole}
                                        status={transaction?.status}
                                        onUpdateStatus={handleUpdateStatus}
                                        transactionId={transaction?.id}
                                        setSnackbar={setSnackbar}
                                    />
                                    {transaction?.status === 'completed' && !reviewSubmitted && (
                                        <ReviewForm onSubmit={(reviewData) => handleReviewSubmit(reviewData, transaction, setSnackbar)} />
                                    )}
                                </StyledPaper>
                            </Grid>

                            {/* RIGHT SIDE: Messaging */}
                            <Grid item xs={12} md={5}>
                                <StyledPaper>
                                    <Typography variant="h6" gutterBottom>
                                        Messages
                                    </Typography>
                                    <ChatMessagesBox
                                        messages={messages}
                                        currentUserId={currentUser.id}
                                        height="400px"
                                    />
                                    <Box display="flex" alignItems="center">
                                        <TextField
                                            fullWidth
                                            variant="outlined"
                                            placeholder={`Message to ${otherUserInfo?.first_name}...`}
                                            value={newMessage}
                                            onChange={(e) => setNewMessage(e.target.value)}
                                            size="small"
                                            sx={{ mr: 1 }}
                                        />
                                        <IconButton onClick={handleSendMessage} color="primary">
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" fill="currentColor" />
                                            </svg>
                                        </IconButton>
                                    </Box>
                                </StyledPaper>
                            </Grid>
                        </Grid>
                        {/* Snackbar notifications can be rendered here */}
                    </StyledContainer>
                );
            }}
        </BookingDetailContainer>
    );
}

export default TransactionDetails;
